import React from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingCart, Check } from 'lucide-react';
import { useCompare } from '../../context/ProductContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { getProductThumb } from '../../data/productImages';
import { toast } from 'sonner';

export function CompareDrawer() {
  const { compareItems, isCompareOpen, setCompareOpen, removeFromCompare, clearCompare } = useCompare();
  const { addItem, items: cartItems } = useCart();
  const { isAuthenticated } = useAuth();

  if (!isCompareOpen || compareItems.length === 0) return null;

  const fields: { label: string; getter: (p: any) => string }[] = [
    { label: 'Marca', getter: (p: any) => p.brand || '-' },
    { label: 'Categoria', getter: (p: any) => p.category || '-' },
    { label: 'Colore', getter: (p: any) => p.color || '-' },
    { label: 'Resa', getter: (p: any) => p.yield || '-' },
    { label: 'Tipo', getter: (p: any) => p.type || '-' },
    // Prezzo: visibile solo ai clienti loggati/approvati (richiesta utente
    // 26/02/2026 — nessun prezzo deve comparire senza login, in nessuna
    // parte del sito, coerente col resto delle pagine prodotto).
    ...(isAuthenticated ? [{ label: 'Prezzo', getter: (p: any) => p.listino10 ? `\u20AC ${(p.listino10 as number).toFixed(2).replace('.', ',')}` : '-' }] : []),
    { label: 'Stock', getter: (p: any) => p.stock != null ? `${Math.floor(p.stock)} pz` : '-' },
  ];

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setCompareOpen(false)} data-testid="compare-drawer">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <h3 className="text-sm font-semibold">Confronta Prodotti ({compareItems.length})</h3>
          <div className="flex items-center gap-2">
            <button onClick={clearCompare} className="text-xs text-muted-foreground hover:text-red-500">Rimuovi tutti</button>
            <button onClick={() => setCompareOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary/60"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 w-24"></th>
                {compareItems.map(p => (
                  <th key={p.sku} className="text-center py-2 px-3">
                    <div className="relative">
                      <button onClick={() => removeFromCompare(p.sku)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-xs hover:bg-red-200"><X className="w-3 h-3" /></button>
                      <Link to={`/prodotti/${p.category}/${p.sku}`} onClick={() => setCompareOpen(false)}>
                        <img src={getProductThumb(p.sku, p.category).replace('w=80', 'w=200')} alt={p.sku} className="w-20 h-20 rounded-lg border bg-white object-contain p-1 mx-auto mb-2" />
                      </Link>
                      <div className="text-xs font-mono font-bold text-[hsl(214,58%,41%)]">{p.sku}</div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[150px] mx-auto">{p.title}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => (
                <tr key={f.label} className="border-b last:border-b-0">
                  <td className="py-2.5 text-xs font-medium text-muted-foreground">{f.label}</td>
                  {compareItems.map(p => (
                    <td key={p.sku} className="py-2.5 text-center text-xs font-medium">{f.getter(p)}</td>
                  ))}
                </tr>
              ))}
              <tr className="border-b">
                <td className="py-2.5 text-xs font-medium text-muted-foreground">Compatibilit\u00E0</td>
                {compareItems.map(p => (
                  <td key={p.sku} className="py-2.5 text-center text-[10px] text-muted-foreground px-2">{p.compatibility}</td>
                ))}
              </tr>
              <tr>
                <td className="py-3"></td>
                {compareItems.map(p => {
                  const inCart = cartItems.some(i => i.sku === p.sku);
                  return (
                    <td key={p.sku} className="py-3 text-center">
                      <button onClick={() => { if (addItem(p) > 0) toast.success(`${p.sku} aggiunto`); }}
                        className={`h-7 px-3 rounded-md text-[10px] font-medium inline-flex items-center gap-1 transition-colors ${inCart ? 'bg-emerald-100 text-emerald-700' : 'bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)]'}`}>
                        {inCart ? <><Check className="w-3 h-3" /> Aggiunto</> : <><ShoppingCart className="w-3 h-3" /> Aggiungi</>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
